/**
 * MobileBottomTabBar — RC7 · M1-A · 2026-08-10 · Amended RC8 Phase 1 · PO 2026-02-16
 *
 * RC8 Phase 1 (PO-ratified 2026-02-16 · Decision D2 · Today-first mobile IA):
 *   1. Today        → dashboard      (current-day surface · consumes /today)
 *   2. Voyage       → journey        (multi-day itinerary + reservations)
 *   3. Briefing     → briefing       (weekly briefing + destination intel)
 *   4. Documents    → documents      (passport · boarding · insurance)
 *   5. Me           → settings       (account · sessions · preferences)
 *
 * Safety remains the persistent Safety FAB / overlay (rendered by
 * `<EmergencyFAB />` in Shell) — not a sixth bottom-navigation slot.
 *
 * Behaviour:
 *   · `md:hidden` — the sidebar dominates on desktop (≥768px), so the
 *     tab bar disappears there. Native builds always render below md.
 *   · Fixed bottom · safe-area padding via `cc-safe-bottom` (respects
 *     the iPhone home indicator and Android nav gestures).
 *   · Backdrop-blur keeps hero imagery legible when scrolled beneath.
 *   · Active tab uses filled brand-navy icon + a small gold accent
 *     dot below the label — the Cruise Command signature.
 *   · Touch targets: 44 × 56 pt minimum (WCAG · Apple HIG · Material).
 *   · Every secondary route (Finance · Route Map · Cruises · Preparation
 *     · Add-Ons detail views · etc.) remains reachable from the
 *     hamburger drawer; the bottom bar is intentionally a curated
 *     primary set for the Today-first travel-operating-system model.
 *
 * RC7 M1-A history: the previous 5-tab set (Home · Journey · Documents
 * · Preparation · Me) is retired by this amendment. `RC8-PHASE-0-
 * DECISION-RECORD.md` D2 supersedes it. The `dashboard` route continues
 * to render the same underlying surface (Voyage Home / Today composed
 * projection) — only the visible label changes here; the underlying
 * route mapping is unchanged.
 */
import React from "react";
import {
  Sun, Compass, BookOpen, FileText, User,
} from "lucide-react";
import { useCruise } from "@/context/CruiseContext";

const NAVY = "#0F2244";
const GOLD = "#C28E42";

const TABS = [
  { key: "today",       label: "Today",       Icon: Sun,       testid: "cc-mobile-tab-today" },
  { key: "journey",     label: "My Itinerary", Icon: Compass,   testid: "cc-mobile-tab-voyage" },
  { key: "briefing",    label: "Briefing",    Icon: BookOpen,  testid: "cc-mobile-tab-briefing" },
  { key: "documents",   label: "Documents",   Icon: FileText,  testid: "cc-mobile-tab-documents" },
  { key: "settings",    label: "Me",          Icon: User,      testid: "cc-mobile-tab-me" },
];

// Today (dashboard) aliases collapse to the Today tab so active state stays
// honest as the app internally normalizes routes.
const DASHBOARD_ALIASES = new Set([
  "today", "dashboard",
]);

// Voyage-adjacent routes collapse under the Voyage tab so the traveller
// sees a consistent active state while working through packing / dining
// / excursions / insurance / add-ons / route-map / finance detail views.
const VOYAGE_ALIASES = new Set([
  "journey", "voyage", "voyage-center", "voyage-map",
  "add-ons", "packing", "dining", "excursions", "insurance",
  "cruise-center", "cruise-planner", "voyage-planner", "planning",
  "route-map", "finance", "voyages",
]);

// Documents tab aliases — all document-facing routes light Documents.
const DOCUMENTS_ALIASES = new Set([
  "documents",
]);

// Me tab aliases — account/session/preference surfaces light Me.
const ME_ALIASES = new Set([
  "settings", "shared-with-me", "memories-center",
]);

export default function MobileBottomTabBar() {
  const { route, setRoute } = useCruise();

  // RC7-M2 · Route → tab-key resolution. Every mobile route collapses
  // deterministically to one of the five tab keys. Prior logic returned
  // synthetic keys ("dashboard" / "add-ons") that no tab actually
  // owned — the active state never lit up. Fixed to return one of
  // {today, journey, briefing, documents, settings}.
  const activeKey = (() => {
    if (DASHBOARD_ALIASES.has(route)) return "today";
    if (VOYAGE_ALIASES.has(route)) return "journey";
    if (DOCUMENTS_ALIASES.has(route)) return "documents";
    if (ME_ALIASES.has(route)) return "settings";
    if (route === "briefing") return "briefing";
    return route;
  })();

  return (
    <nav
      data-testid="cc-mobile-bottom-tab-bar"
      aria-label="Primary"
      className="md:hidden fixed bottom-0 inset-x-0 z-30 cc-safe-bottom bg-white/90 backdrop-blur-md border-t border-slate-200/70"
    >
      <ul className="flex items-stretch">
        {TABS.map(({ key, label, Icon, testid }) => {
          const isActive = key === activeKey;
          return (
            <li key={key} className="flex-1">
              <button
                type="button"
                onClick={() => setRoute(key)}
                data-testid={testid}
                aria-current={isActive ? "page" : undefined}
                aria-label={label}
                className={`w-full min-h-[56px] min-w-[44px] pt-2 pb-1.5 flex flex-col items-center justify-center gap-0.5 transition-colors active:bg-slate-100 ${
                  isActive ? "text-[#0F2244]" : "text-[#64748B]"
                }`}
              >
                {/* Icon */}
                <Icon
                  className={`w-[22px] h-[22px] transition-transform ${
                    isActive ? "scale-105" : ""
                  }`}
                  strokeWidth={isActive ? 2.4 : 1.8}
                  style={isActive ? { color: NAVY } : undefined}
                />
                {/* Label */}
                <span
                  className={`text-[10px] leading-none tracking-[0.02em] ${
                    isActive ? "font-bold" : "font-medium"
                  }`}
                >
                  {label}
                </span>
                {/* Active accent — Cruise Command signature gold dot. */}
                <span
                  aria-hidden="true"
                  className={`mt-0.5 h-1 w-1 rounded-full transition-opacity ${
                    isActive ? "opacity-100" : "opacity-0"
                  }`}
                  style={{ background: GOLD }}
                />
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
